import * as React from 'react'
import { spacerAlignItems } from '@amzn/dvui/dist/Spacer'
import { Button, Purchase } from '@amzn/dvui/dist/Button'
import { Input } from '@amzn/dvui/dist/Input'
import { MessageBlock, MessageTypes } from '@amzn/dvui/dist/Block'
import { RedeemCodeInput, RequestContext } from '@utils'
import { LocalizerProps, TextLocalizer, withLocalization } from '../localizer/localizer'
import { StringId} from '../localizer/stringId'
import { RecordRefTag } from '../recordReftag/recordReftag'
import * as Types from 'DVWebNodeWpsRuntime'
import BalanceCodeType = Types.atv.wps.BalanceCodeType
import { wpsClient } from '../../utils/wps'
import * as st from './giftCardModal.module.scss'
import { safeBoolean } from '../../utils/utils'

const LOG_PREFIX = 'DVWebNodeDetail-RedeemCode'

export const videoHelpUrl = '/videohelp'

const wrongCodeStringAttrs = {
  video_support_website_url: {
    href: videoHelpUrl
  }
}

export const enum CodeSubmissionFormStatus {
  Ready,
  Failed,
  Success
}

export interface ICodeSubmissionFormProps {
  csrfToken: string
  updateBalance: (balanceTypeList: BalanceCodeType[]) => Promise<any>
  requestContext: RequestContext
  hideInput?: boolean
}

type LocalizedCodeSubmissionFormProps = LocalizerProps & ICodeSubmissionFormProps

interface CodeSubmissionFormState {
  disableInputs: boolean
  code: string
  formStatus: CodeSubmissionFormStatus
}

class LocalizedCodeSubmissionForm extends React.Component<LocalizedCodeSubmissionFormProps, CodeSubmissionFormState> {

  constructor(props: LocalizedCodeSubmissionFormProps) {
    super(props)
    this.state = {
      disableInputs: false,
      code: '',
      formStatus: CodeSubmissionFormStatus.Ready
    }
  }

  handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      code: event.target.value,
      formStatus: CodeSubmissionFormStatus.Ready
    })
  }

  onDismiss = () => {
    this.setState({
      formStatus: CodeSubmissionFormStatus.Ready
    })
  }

  handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    this.setState({
      disableInputs: true,
    })
    this.redeemCode()
  }

  redeemCode = async () => {
    try {
      const request: RedeemCodeInput = {
        context: this.props.requestContext,
        csrfToken: this.props.csrfToken,
        code: this.state.code
      }
      const response = await wpsClient.RedeemCode(request)
      if (safeBoolean(response.success)) {
        this.setState({ formStatus: CodeSubmissionFormStatus.Success })
        await this.props.updateBalance([response.balanceType])
      } else {
        this.setState({ formStatus: CodeSubmissionFormStatus.Failed })
      }
    } catch (e) {
      this.props.logger
        .error(`${LOG_PREFIX} - RedeemCode request failed`, e)
        .count(LOG_PREFIX, 1, true)
      this.setState({ formStatus: CodeSubmissionFormStatus.Failed })
    }
    this.setState({ disableInputs: false })
  }

  render() {
    const { getString, csrfToken, hideInput } = this.props
    const { formStatus, code, disableInputs } = this.state
    const inputsDisabled = !csrfToken || disableInputs

    return (
      <>
        {!hideInput &&
        <form onSubmit={this.handleSubmit}>
            <label htmlFor='dv-gc-input' className={st.codeSubmissionFormLabel}>
                <TextLocalizer stringID={StringId.DV_DP_UB_GC_enter_code}/>
            </label>
            <div className={st.codeSubmissionForm}>
                <Input
                    className={st.codeSubmissionFormInput}
                    type='text'
                    placeholder={getString(StringId.DV_DP_GC_code_input_placeholder)}
                    autoComplete='off'
                    id='dv-gc-input'
                    value={code}
                    onChange={this.handleInput}
                    required={true}
                    disabled={inputsDisabled}
                />
                <div className={st.codeSubmissionFormButton}>
                    <RecordRefTag refTag={'atv_dp_apply_gcpc'}>
                        <Button
                            use={Purchase}
                            type='submit'
                            className={spacerAlignItems.center}
                            disabled={inputsDisabled}>
                            <TextLocalizer stringID={StringId.DV_DP_UB_GC_popup_apply}/>
                        </Button>
                    </RecordRefTag>
                </div>
            </div>
        </form>
        }
        <CodeSubmissionFormMessageBlock formStatus={formStatus} onDismiss={this.onDismiss}/>
      </>
    )
  }
}

interface CodeSubmissionFormMessageBlockProps {
  formStatus: CodeSubmissionFormStatus
  onDismiss?: () => void
}

const blockProps = { className: `${st.giftCardModalMessageBlock} ${st.codeSubmissionFormMessageBlock}`, heading: '', dismissable: true }

export const CodeSubmissionFormMessageBlock = ({ formStatus, onDismiss }: CodeSubmissionFormMessageBlockProps) => {
  switch (formStatus) {
    case CodeSubmissionFormStatus.Failed:
      return (
        <div id='codeFailedBlock'>
          <MessageBlock type={MessageTypes.warning} {...blockProps} onDismiss={onDismiss}>
            <TextLocalizer stringID={StringId.DV_DP_GC_wrong_code} dvStringAttrs={wrongCodeStringAttrs}/>
          </MessageBlock>
        </div>)
    case CodeSubmissionFormStatus.Success:
      return (
        <div id={'codeSuccessBlock'}>
          <MessageBlock type={MessageTypes.success} {...blockProps}>
            <TextLocalizer stringID={StringId.DV_DP_UB_GC_success_message}/>
          </MessageBlock>
        </div>)
    default:
      return null
  }
}

export const CodeSubmissionForm = withLocalization(LocalizedCodeSubmissionForm)